// Owns everything needed to show and drive one MFA device's live phone screen:
// the device + connection-log data, the realtime log socket, and the noVNC
// (VNC-over-WebSocket) session itself. Both the full-page connect view and the
// floating panel card consume this hook, so all the fiddly connection logic
// lives here once instead of being duplicated.
import { useState, useEffect, useRef, useCallback } from 'react';
import RFBModule from '@novnc/novnc/lib/rfb';

import store from 'app-store';
import { useSocket } from 'services/socket';

import { useMfaDevice, useMfaConnectionLog } from './use-mfa-device';
import buildDotCursor from '../utils/dot-cursor';

// @novnc ships as either a CommonJS or ES module depending on the build.
const RFB = RFBModule.default || RFBModule;

// Opens a VNC session for `device` inside `element` and returns the RFB handle.
function openVncSession(device, element) {
  const token = store.getState().auth.token;
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const url = `${protocol}//${window.location.host}/api/mfa/proxy/${device.id}?token=${encodeURIComponent(token)}`;

  const rfb = new RFB(element, url, { wsProtocols: ['binary'] });
  rfb.scaleViewport = true;
  rfb.resizeSession = false;
  rfb.showDotCursor = true;
  RFB.cursors.dot = buildDotCursor();
  return rfb;
}

// Makes Cmd/Ctrl+V paste the clipboard into the VNC session's focused field.
// Returns a cleanup function. `isActiveRef` gates it so that when several cards
// are open at once, only the focused one reacts to the paste shortcut.
function attachClipboardPaste(rfb, isActiveRef) {
  const isMac = /Mac|iPod|iPhone|iPad/.test(navigator.platform);

  // The clipboard content reaches the phone, but the focused text field only
  // pastes it once we also send an actual Ctrl+V keystroke.
  const sendCtrlV = () => {
    const CTRL_L = 0xffe3;
    const V = 0x0076;
    rfb.sendKey(CTRL_L, 'ControlLeft', true);
    rfb.sendKey(V, 'KeyV');
    rfb.sendKey(CTRL_L, 'ControlLeft', false);
  };

  const onKeyDown = async (event) => {
    if (!isActiveRef.current) return;

    const isPasteShortcut = (isMac ? event.metaKey : event.ctrlKey) && event.key.toLowerCase() === 'v';
    if (!isPasteShortcut) return;

    event.preventDefault();
    event.stopPropagation();

    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        rfb.clipboardPasteFrom(text);
        setTimeout(sendCtrlV, 100); // give the server a tick to apply the clipboard
      }
    } catch (err) {
      console.warn('Clipboard read failed:', err);
    }
  };

  document.addEventListener('keydown', onKeyDown, true);
  return () => document.removeEventListener('keydown', onKeyDown, true);
}

export default function useMfaConnection({ deviceId, isActive = true }) {
  // --- Data ---------------------------------------------------------------
  const { data: device, isLoading } = useMfaDevice(deviceId);
  const {
    data: connectionEntries = [],
    isLoading: isLoadingLog,
    refetch: refetchLog
  } = useMfaConnectionLog(deviceId);

  const [status, setStatus] = useState('disconnected');
  const [resolution, setResolution] = useState(null);

  // The backend pings this socket (empty payload) whenever this device's
  // connection log changes; we just refetch it.
  const refetchLogRef = useRef(refetchLog);
  useEffect(() => { refetchLogRef.current = refetchLog; }, [refetchLog]);
  const onLogChanged = useCallback(() => refetchLogRef.current && refetchLogRef.current(), []);
  useSocket(`mfaconnectionlog/${deviceId}`, onLogChanged, onLogChanged);

  // --- VNC session --------------------------------------------------------
  // `connect` is a stable callback that reconnect/disconnect buttons and the
  // container ref all call. It reads the latest device / active flag through
  // refs so it never needs to be recreated (which would drop the connection).
  const containerRef = useRef(null);   // the <div> noVNC renders into
  const rfbRef = useRef(null);         // the live RFB session, or null
  const deviceRef = useRef(null);      // latest device data
  const isActiveRef = useRef(isActive); // latest focused-state, for paste gating

  useEffect(() => { deviceRef.current = device || null; }, [device]);
  useEffect(() => { isActiveRef.current = isActive; }, [isActive]);

  const disconnect = useCallback(() => {
    if (rfbRef.current) {
      rfbRef.current.disconnect();
      rfbRef.current = null;
    }
    setStatus('disconnected');
  }, []);

  const connect = useCallback((element) => {
    const currentDevice = deviceRef.current;
    const target = element || containerRef.current;
    if (!target || !currentDevice || !store.getState().auth.token) return;
    if (rfbRef.current) disconnect();

    setStatus('connecting');

    try {
      const rfb = openVncSession(currentDevice, target);
      let detachPaste = null;

      rfb.addEventListener('connect', () => {
        setStatus('connected');
        detachPaste = attachClipboardPaste(rfb, isActiveRef);
      });

      rfb.addEventListener('resize', (event) => {
        setResolution({ width: event.detail.width, height: event.detail.height });
      });

      rfb.addEventListener('disconnect', (event) => {
        setStatus(event.detail.clean ? 'disconnected' : 'error');
        rfbRef.current = null;
        if (detachPaste) detachPaste();
      });

      rfbRef.current = rfb;
    } catch (err) {
      setStatus('error');
    }
  }, [disconnect]);

  // Connect as soon as both the device data and the container <div> are ready.
  // Whichever arrives last triggers it: the container via this callback ref,
  // the device via the effect below.
  const setContainerRef = useCallback((element) => {
    containerRef.current = element;
    if (element && deviceRef.current && !rfbRef.current) connect(element);
  }, [connect]);

  useEffect(() => {
    if (device && containerRef.current && !rfbRef.current) connect();
  }, [device, connect]);

  // Tear the session down on unmount — this is what makes closing a card (or
  // leaving the page) actually disconnect the phone.
  useEffect(() => () => {
    if (rfbRef.current) {
      rfbRef.current.disconnect();
      rfbRef.current = null;
    }
  }, []);

  return {
    device,
    isLoading,
    status,
    resolution,
    connectionEntries,
    isLoadingLog,
    setContainerRef,
    connect,
    disconnect
  };
}
